import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { loucaApi, buildImageUrl } from '../services/loucaApi';
import { normalizeAnalysisWorkflow, getWorkflowBadge } from '../utils/analysisWorkflow';

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const SectionCard = ({ title, description, children }) => (
  <section className="bg-slate-800 border border-orange-500/30 rounded-2xl p-6 space-y-4">
    <div>
      <h2 className="text-xl font-semibold text-white">{title}</h2>
      {description && <p className="text-sm text-slate-300 mt-1">{description}</p>}
    </div>
    <div className="bg-slate-900/60 rounded-xl border border-slate-700/60 p-4">{children}</div>
  </section>
);

const InspectionDevView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await loucaApi.getInspection(id);
        if (response.status === 'success') {
          setInspection(response.data);
        } else {
          setError(response.message || 'Erreur lors de la récupération');
        }
      } catch (err) {
        setError(err.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </Layout>
    );
  }

  if (error || !inspection) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error || 'Inspection non trouvée'}</p>
          <button
            onClick={() => navigate('/inspections')}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg"
          >
            Retour
          </button>
        </div>
      </Layout>
    );
  }

  const workflowModel = normalizeAnalysisWorkflow(inspection);
  const workflow = workflowModel.raw || {};
  const pipelineValue = inspection?.analysis_workflow?.pipeline || workflowModel.pipeline;
  const pipelineBadge = getWorkflowBadge({ ...workflowModel, pipeline: pipelineValue });
  const processedImages = workflowModel.media?.frames || [];
  const yoloResults = workflow.yolo_results || {};
  const damages = workflowModel.damages || [];
  const outlinedImages = (workflowModel.media?.outlined || []).map((img) => ({
    ...img,
    url: buildImageUrl(img.url || img.path),
    filename: img.filename || img.damage_id || img.url,
  }));
  const isYoloWorkflow = pipelineValue === 'yolo_segmentation';
  const rawReport = workflowModel.report || inspection?.report_v02 || {};
  const rawReportsList = ensureArray(rawReport.reports);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 pb-20">
        <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Vue développeur</p>
            <h1 className="text-3xl font-bold text-white">Inspection #{id}</h1>
            <p className="text-sm text-slate-400">Workflow : {pipelineBadge}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/inspection/${id}`)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Vue synthèse
            </button>
            <button
              onClick={() => navigate('/inspections')}
              className="px-4 py-2 bg-orange-500/20 border border-orange-500/40 text-orange-200 rounded-lg hover:bg-orange-500/30"
            >
              Retour liste
            </button>
          </div>
        </header>

        <SectionCard
          title="Vidéo source"
          description="Lien direct vers la vidéo extérieure analysée."
        >
          {inspection.videos && inspection.videos.length > 0 ? (
            <ul className="space-y-2 text-sm">
              {inspection.videos.map((video) => (
                <li key={video.path}>
                  <a
                    href={buildImageUrl(video.url || video.path)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-300 hover:text-orange-200 underline"
                  >
                    {video.angle} – {video.path}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-300 text-sm">Aucune vidéo associée</p>
          )}
        </SectionCard>

        <SectionCard
          title="Images extraites"
          description="Toutes les images clés analysées par le pipeline"
        >
          {processedImages.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {processedImages.map((img) => (
                <a
                  key={img.filename || img.path}
                  href={buildImageUrl(img.url || img.path)}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-slate-900 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <img
                    src={buildImageUrl(img.url || img.path)}
                    alt={img.filename}
                    className="w-full h-40 object-cover group-hover:opacity-90 transition"
                    loading="lazy"
                  />
                  <div className="px-3 py-2 text-xs text-slate-300 truncate">
                    <div className="font-medium truncate">{img.filename || img.path}</div>
                    <div className="text-slate-400 mt-1">
                      {img.width && img.height ? `${img.width}×${img.height}` : '—'}
                      {typeof img.timestamp_s === 'number' && (
                        <span className="ml-2">t={img.timestamp_s}s</span>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-slate-300 text-sm">Aucune image pour cette inspection.</p>
          )}
        </SectionCard>

        {rawReportsList.length > 0 && (
          <SectionCard
            title="Rapport LLM (brut Gemini)"
            description="Payload tel que renvoyé par le modèle, avant enrichissement backend"
          >
            <div className="space-y-4 text-sm text-slate-200">
              <p>{rawReportsList.length} rapport(s) dans report_v02.reports</p>
              <details className="bg-slate-900/70 border border-slate-700 rounded-lg p-4">
                <summary className="cursor-pointer text-orange-300">Afficher le JSON brut</summary>
                <pre className="mt-3 text-xs max-h-[32rem] overflow-auto text-slate-200">
                  {JSON.stringify(rawReport, null, 2)}
                </pre>
              </details>
            </div>
          </SectionCard>
        )}

        {isYoloWorkflow && (
          <SectionCard
            title="Rapport YOLO"
            description="Résultats bruts de la détection de dommages"
          >
            <div className="space-y-4 text-sm text-slate-200">
              <p>Total images : {yoloResults.total_images || 0}</p>
              <p>Total détections : {yoloResults.total_detections || 0}</p>
              <details className="bg-slate-900/70 border border-slate-700 rounded-lg p-4">
                <summary className="cursor-pointer text-orange-300">Afficher le JSON complet</summary>
                <pre className="mt-3 text-xs max-h-96 overflow-auto text-slate-200">
                  {JSON.stringify(yoloResults, null, 2)}
                </pre>
              </details>
            </div>
          </SectionCard>
        )}

        <SectionCard
          title="Rapport LLM"
          description="Dommages synthétisés et images détourées"
        >
          <div className="space-y-6 text-sm text-slate-200">
            {damages.length === 0 ? (
              <p>Aucun dommage synthétisé.</p>
            ) : (
              <div className="space-y-4">
                {damages.map((damage, index) => (
                  <div key={`${damage.part}-${index}`} className="border border-slate-700 rounded-xl p-4 bg-slate-900/70">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-white">{damage.part}</h3>
                      <span className="text-xs uppercase tracking-wide text-slate-400">
                        {damage.part_id || 'Inconnue'}
                      </span>
                    </div>
                    <p className="text-slate-300 mt-1">
                      {damage.type} • Sévérité {damage.severity} • Confiance {Math.round((damage.confidence || 0) * 100)}%
                    </p>
                    {damage.estimated_cost && (
                      <p className="text-orange-200 mt-2">
                        Total estimé : {(damage.estimated_cost.total || 0).toFixed(2)} €
                      </p>
                    )}
                    {damage.outlined_image_url && (
                      <div className="mt-3">
                        <a
                          href={buildImageUrl(damage.outlined_image_url)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-orange-300 hover:text-orange-200 underline"
                        >
                          Image détourée ↗
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <details className="bg-slate-900/70 border border-slate-700 rounded-lg p-4">
              <summary className="cursor-pointer text-orange-300">JSON complet (LLM)</summary>
              <pre className="mt-3 text-xs max-h-96 overflow-auto text-slate-200">
                {JSON.stringify(workflow.llm_synthesis || {}, null, 2)}
              </pre>
            </details>
          </div>
        </SectionCard>

        <SectionCard
          title="Images détourées"
          description="Rendu final envoyé au rapport"
        >
          {outlinedImages.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {outlinedImages.map((image) => (
                <a
                  key={image.filename}
                  href={image.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group block bg-slate-900 border border-slate-700 rounded-xl overflow-hidden"
                >
                  <img
                    src={image.url}
                    alt={image.filename}
                    className="w-full h-40 object-cover group-hover:opacity-90 transition"
                    loading="lazy"
                  />
                  <div className="px-3 py-2 text-xs text-slate-300 truncate">
                    {image.filename}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-slate-300 text-sm">Aucune image détourée disponible.</p>
          )}
        </SectionCard>
      </div>
    </Layout>
  );
};

export default InspectionDevView;
